import type { UserAccountAttributes as GQLInput, UserAttributes as GQLUserInput } from '../../../generated/api.graphql';
import { take } from '../../../util';

/**
 * Alias, not a mirror. AppSchema's `UserAccountAttributes` is already the shape the Team form
 * builds, so a domain restatement would map nothing.
 *
 * Note what is *not* on it: `accountId`. AppSchema scopes the mutation by the
 * `X-VP-Account-Id` header, unlike the legacy payload the Team pages used to send.
 */
export type UserAccountInput = GQLInput;

/** The scalars. `user` is nested and serialized separately, below. */
const SCALAR_FIELDS = ['id', 'teamRoleId'] as const;

const USER_FIELDS = ['id', 'email', 'name'] as const;

/**
 * Compile-time exhaustiveness guards, one per level. `take()` errors on a misspelled key but
 * not on a *missing* one, so without these a field added to either input by a later codegen
 * run would silently stop persisting. This turns that into a build failure.
 */
type _AllGqlFieldsListed =
  [Exclude<keyof GQLInput, (typeof SCALAR_FIELDS)[number] | 'user'>] extends [never] ? true : never;
const _allGqlFieldsListed: _AllGqlFieldsListed = true;
void _allGqlFieldsListed;

type _AllUserFieldsListed =
  [Exclude<keyof GQLUserInput, (typeof USER_FIELDS)[number]>] extends [never] ? true : never;
const _allUserFieldsListed: _AllUserFieldsListed = true;
void _allUserFieldsListed;

/**
 * `take()` omits undefined keys rather than emitting them, which is what keeps an unset `id`
 * off the create path — `id` is the only thing that tells this mutation update from create.
 *
 * `teamRoleId` is `Int!`, so an unset role is dropped here and fails as a *variable*
 * validation error rather than a field error. That matches the legacy input, which typed it
 * the same way; the form keeps the field required.
 */
export const toGqlInput = (input: UserAccountInput): GQLInput => ({
  ...take<GQLInput, GQLInput>(input, ...SCALAR_FIELDS),
  ...(input.user ? { user: take<GQLUserInput, GQLUserInput>(input.user, ...USER_FIELDS) } : {}),
});
